import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, combineLatest, interval } from 'rxjs';
import { distinctUntilChanged, map, share } from 'rxjs/operators';
import { Settings } from '../core/settings';
import * as moment from 'moment';
import { HttpClient } from '@angular/common/http';

import { OktaAuth, IDToken, AccessToken, Tokens } from '@okta/okta-auth-js';
import { UrlBuilder } from '../core/url-builder/url-builder';
import { isEmpty } from 'lodash';

export interface AuthInfo {
    isAuthenticated: boolean;
    idToken: IDToken | null;
    accessToken: AccessToken | null;
}

@Injectable()
export class AuthenticationState {

    public baseElement = document.querySelector('base');

    public clientId = this.settings.clientId;
    public redirectUrl = !!this.baseElement ? this.baseElement.href : window.location.href;
    public issuer = this.settings.issuer;
    public readonly oktaAuth: OktaAuth;

    private readonly authInfoSource: ReplaySubject<AuthInfo> = new ReplaySubject<AuthInfo>(1);
    private readonly expirationSource: Observable<ExpirationSnapshot>;

    private scopes: string[] = [
        'openid',
        'email',
        'profile'
    ];

    constructor(
        private settings: Settings,
        private readonly http: HttpClient,
        private urlBuilder: UrlBuilder
    ) {
        const baseElement = document.querySelector('base');
        this.redirectUrl = !!baseElement ? baseElement.href : window.location.href;
        this.issuer = this.settings.issuer;

        this.oktaAuth = new OktaAuth({
            issuer: this.issuer,
            responseType: ['code'],
            clientId: this.clientId,
            redirectUri: this.redirectUrl,
            pkce: true,
            scopes: this.scopes,
            tokenManager: {
                storage: 'memory'
            }
        });

        this.loadInitialState();

        this.expirationSource = this.createExpirationSource().pipe(share());
    }

    public get authInfo() {
        return this.authInfoSource.asObservable();
    }

    public get tokenExpiration() {
        return this.expirationSource;
    }

    public async refresh() {
        const token = await this.oktaAuth.token.getWithoutPrompt({
            clientId: this.clientId,
            scopes: this.scopes
        });

        this.setToken(token.tokens);
    }

    public async login() {
        //save current route for equery creation deeplink CLX-4896
        this.oktaAuth.setOriginalUri(window.location.href);

        //okta authjs redirect code
        if (this.oktaAuth.isLoginRedirect()) {
            this.oktaAuth.token.parseFromUrl()
                .then(data => {
                    this.setToken(data.tokens);
                });
        } else {
            this.oktaAuth.token.getWithRedirect({
                responseType: ['code']
            });
        }
    }

    public async logout() {
        //await this.oktaAuth.session.close();
        await this.oktaAuth.tokenManager.clear();
        this.setToken(null);
        await this.oktaAuth.signOut({
            revokeAccessToken: true,
            revokeRefreshToken: true,
            postLogoutRedirectUri: window.location.origin
        });
    }

    public async isAuthenticated() {

        let successfulAuthentication = false;
        try {
            const tokens = await this.oktaAuth.tokenManager.getTokens();

            if (!tokens.idToken) {
                const token = await this.oktaAuth.token.getWithoutPrompt({
                    scopes: this.scopes,
                    responseType: ['code']
                });

                this.setToken(token.tokens);
            }

            successfulAuthentication = true;

        } catch (e) {
            console.debug('Error while trying to fetch auth', e);
        }

        if (!successfulAuthentication) {
            this.setToken(null);
        }

        return successfulAuthentication;
    }

    private async loadInitialState() {
        this.isAuthenticated();
    }

    private setToken(tokens: Tokens | null) {
        console.debug('store auth result called', tokens);

        if (!tokens) {
            this.authInfoSource.next({ isAuthenticated: false, idToken: null, accessToken: null });
        } else {
            this.authInfoSource.next({ isAuthenticated: true, idToken: tokens.idToken, accessToken: tokens.accessToken });
            //redirect to equery creation deeplink CLX-4896
            const origurl = this.oktaAuth.getOriginalUri();
            if (origurl !== null && !isEmpty(origurl) && origurl.includes('new-e-query')) {
                this.urlBuilder.esa().navigateToUrl(origurl);
            }
        }
    }

    private createExpirationSource() {

        return combineLatest(this.authInfo, interval(1000))
            .pipe(
                map(([authInfo]) => new ExpirationSnapshot(authInfo)),
                distinctUntilChanged((p, q) => {
                    return p.isExpired === q.isExpired && p.authInfo === q.authInfo;
                }));
    }
}

export class ExpirationSnapshot {
    public readonly isExpired: boolean | null = null;
    public readonly timeLeft: number;

    constructor(public readonly authInfo: AuthInfo) {

        if (authInfo.idToken && authInfo.idToken.idToken && authInfo.isAuthenticated) {
            const unixNow = moment.utc().unix();
            const unixLeft = authInfo.idToken.expiresAt - unixNow;
            this.isExpired = unixLeft <= 0;
            if (!this.isExpired) {
                this.timeLeft = unixLeft;
            }
        }
    }
}